import {
  collection,
  DocumentData,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  QuerySnapshot,
  startAfter,
  where,
} from "firebase/firestore";
import { useInfiniteQuery } from "react-query";
import {
  convertLessonExerciseSnapToILessonExercise,
  ILessonExercise,
} from "../../types/firestoreMappings";
import { collectionPaths } from "../collectionPaths";
import { settledLogger } from "../settledLogger";
import { useGetLessonExerciseFetch } from "./useGetLessonExerciseQuery";

export interface ListLessonExercisesQueryParams {
  courseId?: string;
  lessonId?: string;
  sortByUpvotes?: boolean;
  topItemId?: string | null;
  numItemsPerPage?: number;
}

const defaultNumItemsPerPage = 3;

export const listLessonExercisesQueryKey = (
  params: ListLessonExercisesQueryParams
) => {
  return ["listLessonExercisesQueryKey", params];
};

export const useListLessonExercisesQuery = (
  params: ListLessonExercisesQueryParams,
  enabled: boolean = true
) => {
  const fetchLessonExercise = useGetLessonExerciseFetch();
  const listLessonExercises = async (vars: {
    pageParam?: {
      lastPage: {
        realData: ILessonExercise[];
        snaps: QuerySnapshot<DocumentData>;
      };
    };
  }) => {
    const { pageParam } = vars;
    const { courseId, lessonId, sortByUpvotes, topItemId, numItemsPerPage } =
      params;
    const numItemsPerPageToUse = numItemsPerPage || defaultNumItemsPerPage;
    let isLastPage = false;
    const db = getFirestore();
    const lessonExercisesCollectionRef = collection(
      db,
      collectionPaths.lessonExercises
    );
    const queryConstraints = [];
    if (sortByUpvotes) {
      queryConstraints.push(orderBy("upvotes", "desc"));
    }
    // Always sort by createdAt by default
    queryConstraints.push(orderBy("createdAt", "desc"));
    if (courseId) {
      queryConstraints.push(where("courseId", "==", courseId));
    }
    if (lessonId) {
      queryConstraints.push(where("lessonId", "==", lessonId));
    }
    // Do pagination stuff
    if (pageParam) {
      const lastSnap =
        pageParam.lastPage.snaps.docs[pageParam.lastPage.snaps.docs.length - 1];
      if (lastSnap) {
        queryConstraints.push(startAfter(lastSnap));
      }
    }
    const q = query(
      lessonExercisesCollectionRef,
      ...queryConstraints,
      limit(numItemsPerPageToUse)
    );
    const qSnapshot = await getDocs(q);
    let lessonExercises = qSnapshot.docs.map((snapshot) =>
      convertLessonExerciseSnapToILessonExercise(snapshot.id, snapshot.data())
    );

    if (lessonExercises.length < numItemsPerPageToUse) {
      isLastPage = true;
    }

    if (topItemId) {
      // Filter out specified top item to avoid duplicates
      lessonExercises = lessonExercises.filter(
        (exercise) => exercise.id !== topItemId
      );
      const isFirstPage = !pageParam;
      if (isFirstPage) {
        // Only add specified top item if first page load, otherwise it duplicates
        const topExercise = await fetchLessonExercise({
          lessonExerciseId: topItemId,
        });
        lessonExercises = [topExercise].concat(lessonExercises);
      }
    }

    return {
      realData: lessonExercises,
      snaps: qSnapshot,
      isLastPage,
    };
  };
  const paginatedResult = useInfiniteQuery(
    listLessonExercisesQueryKey(params),
    listLessonExercises,
    {
      getNextPageParam: (lastPage, allPages) => {
        return {
          lastPage,
          allPages,
        };
      },
      onSettled: settledLogger("useListLessonExercisesQuery"),
      enabled,
    }
  );

  let realDataAllPages: ILessonExercise[] = [];
  let isNoMoreToFetch = true;
  if (paginatedResult.data) {
    paginatedResult.data.pages.forEach((page) => {
      realDataAllPages = realDataAllPages.concat(page.realData);
    });
    isNoMoreToFetch =
      paginatedResult.data.pages[paginatedResult.data.pages.length - 1]
        .isLastPage;
  }

  return {
    ...paginatedResult,
    data: realDataAllPages,
    isNoMoreToFetch,
  };
};
